/**
 * Предобработка изображений для OCR.
 * Пайплайн: загрузка → EXIF → сжатие до 1500px → контраст → (опц.) медианный фильтр → (опц.) бинаризация.
 */

export interface PreprocessOptions {
  maxWidth?: number;
  maxHeight?: number;
  jpegQuality?: number;
  applyContrast?: boolean;
  applyMedianFilter?: boolean;
  applyBinarization?: boolean;
}

const DEFAULT_OPTIONS: PreprocessOptions = {
  maxWidth: 1500,
  maxHeight: 2000,
  jpegQuality: 0.92,
  applyContrast: true,
  applyMedianFilter: false,
  applyBinarization: false,
};

export async function preprocessImageForOCR(
  file: File,
  options?: PreprocessOptions
): Promise<Blob> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const img = await loadImage(file);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  let { width, height } = img;

  if (width > opts.maxWidth!) {
    height = (height * opts.maxWidth!) / width;
    width = opts.maxWidth!;
  }
  if (height > opts.maxHeight!) {
    width = (width * opts.maxHeight!) / height;
    height = opts.maxHeight!;
  }

  canvas.width = Math.round(width);
  canvas.height = Math.round(height);

  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  if (opts.applyContrast) {
    applyContrastStretching(imageData.data);
  }

  if (opts.applyMedianFilter) {
    applyMedianFilter(imageData.data, canvas.width, canvas.height);
  }

  if (opts.applyBinarization) {
    applyAdaptiveThreshold(imageData.data, canvas.width, canvas.height);
  }

  ctx.putImageData(imageData, 0, 0);

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob!),
      "image/jpeg",
      opts.jpegQuality
    );
  });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function applyContrastStretching(data: Uint8ClampedArray): void {
  let minR = 255,
    maxR = 0;
  let minG = 255,
    maxG = 0;
  let minB = 255,
    maxB = 0;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    if (r < minR) minR = r;
    if (r > maxR) maxR = r;
    if (g < minG) minG = g;
    if (g > maxG) maxG = g;
    if (b < minB) minB = b;
    if (b > maxB) maxB = b;
  }

  const rangeR = maxR - minR || 1;
  const rangeG = maxG - minG || 1;
  const rangeB = maxB - minB || 1;

  for (let i = 0; i < data.length; i += 4) {
    data[i] = ((data[i] - minR) / rangeR) * 255;
    data[i + 1] = ((data[i + 1] - minG) / rangeG) * 255;
    data[i + 2] = ((data[i + 2] - minB) / rangeB) * 255;
  }
}

function applyMedianFilter(
  data: Uint8ClampedArray,
  width: number,
  height: number
): void {
  const radius = 1;
  const copy = new Uint8ClampedArray(data);

  for (let y = radius; y < height - radius; y++) {
    for (let x = radius; x < width - radius; x++) {
      const idx = (y * width + x) * 4;
      const neighbors: number[][] = [[], [], []];

      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const ni = ((y + dy) * width + (x + dx)) * 4;
          neighbors[0].push(copy[ni]);
          neighbors[1].push(copy[ni + 1]);
          neighbors[2].push(copy[ni + 2]);
        }
      }

      neighbors.forEach((channel, i) => {
        channel.sort((a, b) => a - b);
        data[idx + i] = channel[Math.floor(channel.length / 2)];
      });
    }
  }
}

function applyAdaptiveThreshold(
  data: Uint8ClampedArray,
  width: number,
  height: number
): void {
  const blockSize = 15;
  const C = 10;

  const gray = new Uint8ClampedArray(width * height);
  for (let i = 0; i < data.length; i += 4) {
    const idx = i / 4;
    gray[idx] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }

  const integral = new Uint32Array((width + 1) * (height + 1));
  for (let y = 0; y < height; y++) {
    let rowSum = 0;
    for (let x = 0; x < width; x++) {
      rowSum += gray[y * width + x];
      const idx = (y + 1) * (width + 1) + (x + 1);
      integral[idx] = integral[y * (width + 1) + (x + 1)] + rowSum;
    }
  }

  const halfBlock = Math.floor(blockSize / 2);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const x1 = Math.max(0, x - halfBlock);
      const x2 = Math.min(width - 1, x + halfBlock);
      const y1 = Math.max(0, y - halfBlock);
      const y2 = Math.min(height - 1, y + halfBlock);

      const count = (x2 - x1 + 1) * (y2 - y1 + 1);
      const sum =
        integral[y2 * (width + 1) + x2] -
        integral[y1 * (width + 1) + x2] -
        integral[y2 * (width + 1) + x1] +
        integral[y1 * (width + 1) + x1];

      const mean = sum / count;
      const threshold = mean - C;

      const idx = (y * width + x) * 4;
      const value = gray[y * width + x] < threshold ? 0 : 255;
      data[idx] = data[idx + 1] = data[idx + 2] = value;
    }
  }
}
